import React, { useEffect, useMemo } from 'react';
import type {
  VesonBunkerQuery,
  VesonBunkerQueryVariables,
  VesonDefaultBunkerPrice,
  VesonRemainingBunker,
  VesonRemainingBunkerOfVoyage,
} from '../../graphql';
import { useVesonBunkerQuery, VesonBunkerDocument } from '../../graphql';
import { hasFeature } from '../../state/zustand';
import { Box, Text } from '../ui';
import LoadingSpinner from '../LoadingSpinner';
import type { EventTimeline } from '../core/Timelines';
import { isTCOrderEventTimeData } from '../core/TimeData';
import { toast } from 'react-toastify';
import type { ApolloClient } from '@apollo/client';

export type VesonRemainingBunkerByExternalShipmentId = Map<
  string,
  VesonRemainingBunkerOfVoyage
>;
export type VesonDefaultBunkerByVesselCode = Map<
  string,
  VesonDefaultBunkerPrice[]
>;
export type VesonBunker = {
  remainingBunkerByExternalShipmentId: VesonRemainingBunkerByExternalShipmentId;
  defaultBunkerByVesselCode: VesonDefaultBunkerByVesselCode;
};

function useIntegrationVesonBunker() {
  const isEnabled = hasFeature('veson_tools');
  const { data, loading, error } = useVesonBunkerQuery({
    skip: !isEnabled,
  });

  const remainingBunker =
    data?.getVesonBunker?.remainingBunkerOfVoyages ?? null;
  const defaultBunker = data?.getVesonBunker?.defaultBunkerOfVessels ?? null;
  const errorMessage = data?.getVesonBunker?.errorMessage ?? null;

  useEffect(() => {
    if (!isEnabled) return;

    if (!loading && error) {
      toast.error(`Error fetching IMOS RoB ${error.message}`);
    }
  }, [error, isEnabled, loading]);

  if (errorMessage) {
    console.error(`Rob error: ${errorMessage}`);
  }

  return {
    isEnabled,
    isLoading: loading,
    remainingBunker,
    defaultBunker,
  };
}

/** For usage in a non-react context  */
export async function fetchIntegrationVesonBunker(
  client: ApolloClient<unknown>,
) {
  const res = await client.query<VesonBunkerQuery, VesonBunkerQueryVariables>({
    query: VesonBunkerDocument,
  });

  return {
    defaultBunker: res.data.getVesonBunker?.defaultBunkerOfVessels ?? null,
    remainingBunker: res.data.getVesonBunker?.remainingBunkerOfVoyages ?? null,
  };
}

export function useVoyageVesonBunker() {
  const { isEnabled, isLoading, remainingBunker, defaultBunker } =
    useIntegrationVesonBunker();

  const vesonBunkerData = useMemo<VesonBunker>(() => {
    return parseIntegrationVesonBunkerResult({
      defaultBunker,
      remainingBunker,
    });
  }, [defaultBunker, remainingBunker]);

  return {
    isEnabled,
    isLoading,
    vesonBunkerData,
  };
}

export function parseIntegrationVesonBunkerResult({
  defaultBunker,
  remainingBunker,
}: {
  defaultBunker: Awaited<
    ReturnType<typeof fetchIntegrationVesonBunker>
  >['defaultBunker'];
  remainingBunker: Awaited<
    ReturnType<typeof fetchIntegrationVesonBunker>
  >['remainingBunker'];
}) {
  const remainingBunkerByExternalShipmentId: VesonRemainingBunkerByExternalShipmentId =
    new Map();
  const defaultBunkerByVesselCode: VesonDefaultBunkerByVesselCode = new Map();

  if (remainingBunker) {
    remainingBunker.forEach((entry) => {
      remainingBunkerByExternalShipmentId.set(entry.voyage, entry);
    });
  }

  if (defaultBunker) {
    defaultBunker.forEach((entry) => {
      defaultBunkerByVesselCode.set(entry.vessel_code, entry.bunker);
    });
  }

  return {
    defaultBunkerByVesselCode,
    remainingBunkerByExternalShipmentId,
  };
}

export function getShipmentVesonRobDetails(
  timeline: EventTimeline,
  focusedOrderIds: Set<number> | null,
  {
    defaultBunkerByVesselCode,
    remainingBunkerByExternalShipmentId,
  }: VesonBunker,
) {
  if (timeline.type !== 'tc' || !timeline.vessel) {
    return undefined;
  }

  let shipmentExternalId: null | string = null;
  timeline.voyageGroups.forEach((vg) => {
    vg.portCalls.forEach((pc) => {
      pc.tds.forEach((td) => {
        if (!isTCOrderEventTimeData(td)) return;
        const { id: orderId, shipment } = td.event.order;
        if (
          focusedOrderIds &&
          focusedOrderIds.has(orderId) &&
          shipment?.externalShipmentId
        ) {
          shipmentExternalId = shipment.externalShipmentId;
        }
      });
    });
  });

  /**
   * If a voyage has RoB information in the endpoint, use this for calculation
   * otherwise, rely on RoB in previous voyage (if this previous voyage has RoB in endpoint)
   */

  const remainingBunkerOfVoyage = shipmentExternalId
    ? remainingBunkerByExternalShipmentId.get(shipmentExternalId)
    : undefined;

  const defaultBunker = timeline.vessel.shortName
    ? defaultBunkerByVesselCode.get(timeline.vessel.shortName)
    : undefined;

  // find nearest rob of previous voyage
  let remainingBunkerOfPreVoyage: undefined | VesonRemainingBunkerOfVoyage =
    undefined;
  if (!remainingBunkerOfVoyage) {
    const firstPortCallIdOfVoyage = timeline.voyageGroups
      .at(0)
      ?.portCalls.at(0)?.id;

    if (timeline.parent && firstPortCallIdOfVoyage) {
      const portCalls = timeline.parent.voyageGroups.flatMap(
        (vg) => vg.portCalls,
      );
      const portCallIdx = portCalls.findIndex(
        (pc) => pc.id === firstPortCallIdOfVoyage,
      );
      if (portCallIdx !== -1) {
        for (let i = portCallIdx - 1; i >= 0; i--) {
          const tds = portCalls[i].tds;
          const tcTimeData = tds.find(isTCOrderEventTimeData);
          if (tcTimeData) {
            const externalShipmentId =
              tcTimeData.event.order.shipment?.externalShipmentId;
            remainingBunkerOfPreVoyage =
              remainingBunkerByExternalShipmentId.get(externalShipmentId ?? '');
            break;
          }
        }
      }
    }
  }

  return {
    defaultBunker,
    remainingBunkerOfVoyage,
    remainingBunkerOfPreVoyage,
  };
}

export function IntegrationVesonRobLoadingIndicator(props: {
  isLoading: boolean;
}) {
  if (!props.isLoading) return null;

  return (
    <Box display="flex" alignItems="center">
      <LoadingSpinner />
      <Text ml={2}>Loading IMOS RoB</Text>
    </Box>
  );
}

export type ImosRobByGrade = Map<string, VesonRemainingBunker[]>;
export function getImosRobByGrade(imosRobInventory: VesonRemainingBunker[]) {
  const robByGrade: ImosRobByGrade = new Map();

  for (let i = imosRobInventory.length - 1; i >= 0; i--) {
    const rob = imosRobInventory[i];
    const currentRobs = robByGrade.get(rob.name);
    if (currentRobs) {
      currentRobs.push(rob);
    } else {
      robByGrade.set(rob.name, [rob]);
    }
  }

  return robByGrade;
}

export function getImosDefaultRobPriceByGrade(
  defaultBunkerPrices: VesonDefaultBunkerPrice[],
) {
  const defaultRobByGradeName: Map<string, number> = new Map();

  defaultBunkerPrices.forEach(({ name, price }) => {
    defaultRobByGradeName.set(name, price);
  });

  return defaultRobByGradeName;
}
